import { create } from 'zustand'
import { sellerCurrencyAPI, type SharedCurrencyConfig } from './sellerApi'

type CurrencyCode = 'INR' | 'USD'
type RateSource = 'manual' | 'live'

interface SellerCurrencyState {
  currency: CurrencyCode
  symbol: string
  usdInrRate: number
  rateSource: RateSource
  setConfig: (next: Partial<Omit<SellerCurrencyState, 'setConfig'>>) => void
}

const DEFAULT_RATE = 83

const normalizeCurrency = (value: unknown): CurrencyCode =>
  String(value ?? '').toUpperCase() === 'USD' ? 'USD' : 'INR'

const normalizeRateSource = (value: unknown): RateSource =>
  String(value ?? '').toLowerCase() === 'live' ? 'live' : 'manual'

const normalizeRate = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_RATE
}

const resolveDefaultSymbol = (currency: CurrencyCode) => (currency === 'USD' ? '$' : 'Rs. ')

export const useSellerCurrencyStore = create<SellerCurrencyState>((set) => ({
  currency: 'INR',
  symbol: 'Rs. ',
  usdInrRate: DEFAULT_RATE,
  rateSource: 'manual',
  setConfig: (next) =>
    set((state) => ({
      ...state,
      ...next,
    })),
}))

export const applySellerCurrencyConfig = (payload?: SharedCurrencyConfig | null) => {
  const currency = normalizeCurrency(payload?.currency)
  const symbol =
    typeof payload?.symbol === 'string' && payload.symbol.trim().length > 0
      ? payload.symbol
      : resolveDefaultSymbol(currency)

  useSellerCurrencyStore.getState().setConfig({
    currency,
    symbol,
    usdInrRate: normalizeRate(payload?.usd_inr_rate),
    rateSource: normalizeRateSource(payload?.rate_source),
  })
}

export const hydrateSellerCurrencyConfig = async (): Promise<void> => {
  try {
    const response = await sellerCurrencyAPI.getConfig()
    applySellerCurrencyConfig(response.data?.data)
  } catch {
    // Keep defaults if currency config request fails.
  }
}

const readCurrencyState = () => useSellerCurrencyStore.getState()

export const convertFromInrToSellerCurrency = (amount: number): number => {
  const value = Number(amount)
  if (!Number.isFinite(value)) {
    return 0
  }

  const { currency, usdInrRate } = readCurrencyState()

  if (currency !== 'USD') {
    return Number(value.toFixed(2))
  }

  return Number((value / usdInrRate).toFixed(2))
}

export const convertSellerCurrencyToInr = (amount: number): number => {
  const value = Number(amount)
  if (!Number.isFinite(value)) {
    return 0
  }

  const { currency, usdInrRate } = readCurrencyState()

  if (currency !== 'USD') {
    return Number(value.toFixed(2))
  }

  return Number((value * usdInrRate).toFixed(2))
}

export const formatSellerCurrency = (
  amount: number,
  options?: {
    fromInr?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    symbol?: string
  },
): string => {
  const value = Number(amount)
  const safeValue = Number.isFinite(value) ? value : 0
  const displayValue = options?.fromInr === false ? safeValue : convertFromInrToSellerCurrency(safeValue)
  const { currency, symbol } = readCurrencyState()
  const resolvedSymbol = options?.symbol ?? symbol
  const locale = currency === 'USD' ? 'en-US' : 'en-IN'

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: options?.minimumFractionDigits,
      maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    })

    return formatter
      .formatToParts(displayValue)
      .map((part) => (part.type === 'currency' ? resolvedSymbol : part.value))
      .join('')
  } catch {
    return `${resolvedSymbol}${displayValue.toFixed(options?.maximumFractionDigits ?? 2)}`
  }
}
